'use client';

import { useRef } from 'react';

import { motion } from 'framer-motion';
import {
  ArrowUp,
  CalendarCheck,
  CircleCheck,
  CreditCard,
  MessageCircle,
  Sparkles,
} from 'lucide-react';

import { cn } from '@kit/ui/utils';

import { ADMIN_DEMO_SIZE, AdminShell } from './admin-shell';
import {
  AnimatedNumber,
  Avatar,
  CheckBadge,
  DEMO_COLORS,
  type DemoProps,
  DemoStage,
  EASE_OUT_EXPO,
  Reveal,
  TapRipple,
  TypingDots,
  localize,
  useDemoTimeline,
} from './demo-kit';

/*
 * Customer profile and admin AI: Sara's history fills in (visits, package
 * balance, attendance, activity), then the owner asks the assistant who has
 * gone quiet and sends a WhatsApp win-back campaign from the answer.
 */

export const CRM_DEMO_SIZE = ADMIN_DEMO_SIZE;

const DURATIONS = [500, 1800, 1800, 1400, 900, 2000, 2800] as const;

const STEP = {
  stats: 1,
  activity: 2,
  typing: 3,
  sent: 4,
  answer: 5,
  campaign: 6,
} as const;

const COPY = {
  label: {
    en: 'Animated demo of the customer CRM: a member profile shows visits, package balance, attendance and activity, then the owner asks the AI assistant who has not visited in three weeks and sends a WhatsApp win-back campaign.',
    ar: 'عرض متحرك لنظام إدارة العملاء: ملف عضوة يعرض الزيارات ورصيد الباقة والحضور والنشاط، ثم يسأل المالك المساعد الذكي عمّن انقطع منذ ثلاثة أسابيع ويرسل حملة استعادة عبر واتساب.',
  },
  title: { en: 'Customers', ar: 'العملاء' },
  subtitle: { en: 'Linen Studio', ar: 'استوديو لينن' },
  name: { en: 'Sara Haddad', ar: 'سارة حداد' },
  member: { en: 'Member', ar: 'عضوة' },
  morning: { en: 'Morning regular', ar: 'تحضر صباحًا' },
  pilates: { en: 'Pilates', ar: 'بيلاتس' },
  message: { en: 'Message', ar: 'مراسلة' },
  visits: { en: 'Visits', ar: 'الزيارات' },
  packages: { en: 'Packages bought', ar: 'الباقات المشتراة' },
  since: { en: 'Member since', ar: 'عضوة منذ' },
  sinceValue: { en: 'Mar 2024', ar: 'مارس 2024' },
  attendance: { en: 'Attendance · last 12 weeks', ar: 'الحضور · آخر 12 أسبوعًا' },
  packName: { en: 'Reformer 10 pack', ar: 'باقة ريفورمر 10' },
  sessionsLeft: { en: '7 of 10 sessions left', ar: 'متبقٍ 7 من 10 جلسات' },
  of10: { en: 'of 10', ar: 'من 10' },
  activity: { en: 'Activity', ar: 'النشاط' },
  booked: { en: 'Booked Reformer Flow · Thu 7:00 AM', ar: 'حجزت ريفورمر فلو · الخميس 7:00 ص' },
  paid: { en: 'Paid with Apple Pay', ar: 'دفعت عبر Apple Pay' },
  attended: { en: 'Attended Reformer Flow', ar: 'حضرت حصة ريفورمر فلو' },
  reminder: { en: 'WhatsApp reminder sent', ar: 'أُرسل تذكير عبر واتساب' },
  t5d: { en: '5d', ar: '5 أيام' },
  t2d: { en: '2d', ar: 'يومان' },
  t3h: { en: '3h', ar: '3 ساعات' },
  ai: { en: 'Flow AI', ar: 'مساعد Flow' },
  aiSub: { en: 'Ask about your business', ar: 'اسأل عن أعمالك' },
  placeholder: { en: 'Ask anything…', ar: 'اسأل أي شيء…' },
  question: { en: "Who hasn't visited in 3 weeks?", ar: 'من لم يزرنا منذ 3 أسابيع؟' },
  answer: { en: '18 members haven\'t visited in 3 weeks.', ar: '18 عضوًا لم يزوروا الاستوديو منذ 3 أسابيع.' },
  more: { en: '+15 more', ar: 'و15 آخرون' },
  send: { en: 'Send win-back on WhatsApp', ar: 'أرسل حملة استعادة' },
  sentLabel: { en: 'Campaign sent to 18 members', ar: 'أُرسلت الحملة إلى 18 عضوًا' },
} as const;

const QUIET_MEMBERS = [
  { initials: 'NF', tone: 0, name: { en: 'Nadia F.', ar: 'نادية ف.' }, days: { en: '23 days', ar: '23 يومًا' } },
  { initials: 'LM', tone: 4, name: { en: 'Lina M.', ar: 'لينا م.' }, days: { en: '25 days', ar: '25 يومًا' } },
  { initials: 'HA', tone: 2, name: { en: 'Huda A.', ar: 'هدى ع.' }, days: { en: '31 days', ar: '31 يومًا' } },
] as const;

const SPARK = [2, 3, 3, 4, 3, 4, 4, 5, 4, 4, 3, 5] as const;
const SPARK_W = 236;
const SPARK_H = 46;

const sparkPoints = SPARK.map((value, index) => [
  (index / (SPARK.length - 1)) * SPARK_W,
  SPARK_H - (value / 5) * (SPARK_H - 6) - 2,
]);
const SPARK_LINE = sparkPoints
  .map(([x, y], index) => `${index ? 'L' : 'M'}${x!.toFixed(1)} ${y!.toFixed(1)}`)
  .join(' ');
const SPARK_AREA = `${SPARK_LINE} L${SPARK_W} ${SPARK_H} L0 ${SPARK_H} Z`;

export function CrmDemo({ locale, className }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS);
  const copy = localize(COPY, locale);
  const step = timeline.step;
  const rtl = locale === 'ar';

  const activity = [
    { Icon: CalendarCheck, text: copy.booked, time: copy.t5d, tint: 'bg-[#e6f0ff] text-[#2c4c80]' },
    { Icon: CreditCard, text: copy.paid, time: copy.t5d, tint: 'bg-[#ede8e2] text-[#5c554b]' },
    { Icon: CircleCheck, text: copy.attended, time: copy.t2d, tint: 'bg-[#ddf5ec] text-[#2f7a63]' },
    { Icon: MessageCircle, text: copy.reminder, time: copy.t3h, tint: 'bg-[#dcf8e6] text-[#1f7a4a]' },
  ];

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <AdminShell
        active="customers"
        title={copy.title}
        subtitle={copy.subtitle}
        locale={locale}
        url="app.linenstudio.com"
        location={copy.subtitle}
      >
        <div className="flex h-full gap-3.5 p-3.5">
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <div className="flex h-[50px] items-center gap-3">
              <Avatar initials="SH" tone={3} size={42} className="ring-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold leading-tight text-[#202020]">{copy.name}</p>
                <div className="mt-1.5 flex gap-1.5">
                  <Tag className="bg-[#e6f0ff] text-[#2c4c80]">{copy.member}</Tag>
                  <Tag className="bg-[#ede8e2] text-[#5c554b]">{copy.morning}</Tag>
                  <Tag className="bg-[#ddf5ec] text-[#2f7a63]">{copy.pilates}</Tag>
                </div>
              </div>
              <span className="flex h-7 items-center gap-1.5 rounded-full border border-[#ebe7e1] px-3 text-[10.5px] text-[#202020]">
                <MessageCircle className="h-3 w-3 text-[#1faa59]" />
                {copy.message}
              </span>
            </div>

            <div className="grid h-[58px] grid-cols-3 gap-2">
              <StatTile label={copy.visits}>
                <AnimatedNumber value={step >= STEP.stats ? 48 : 0} />
              </StatTile>
              <StatTile label={copy.packages}>
                <AnimatedNumber value={step >= STEP.stats ? 6 : 0} />
              </StatTile>
              <StatTile label={copy.since}>{copy.sinceValue}</StatTile>
            </div>

            <div className="grid h-[104px] grid-cols-[1.45fr_1fr] gap-2">
              <div className="rounded-[12px] border border-[#ebe7e1] p-3">
                <p className="text-[10.5px] text-[#8a857c]">{copy.attendance}</p>
                <svg
                  width={SPARK_W}
                  height={SPARK_H}
                  viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
                  className="mt-2.5 overflow-visible"
                  style={{ transform: rtl ? 'scaleX(-1)' : undefined }}
                  aria-hidden="true"
                >
                  <motion.path
                    d={SPARK_AREA}
                    fill="#e6f0ff"
                    initial={false}
                    animate={{ opacity: step >= STEP.stats ? 1 : 0 }}
                    transition={{ duration: 0.8, delay: step >= STEP.stats ? 0.7 : 0 }}
                  />
                  <motion.path
                    d={SPARK_LINE}
                    fill="none"
                    stroke="#3f6099"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={false}
                    animate={{ pathLength: step >= STEP.stats ? 1 : 0 }}
                    transition={{ duration: step >= STEP.stats ? 1.3 : 0.2, ease: EASE_OUT_EXPO }}
                  />
                </svg>
              </div>

              <div className="flex items-center gap-3 rounded-[12px] border border-[#ebe7e1] p-3">
                <div className="relative h-[62px] w-[62px] shrink-0">
                  <svg width="62" height="62" viewBox="0 0 62 62" className="-rotate-90" aria-hidden="true">
                    <circle cx="31" cy="31" r="26" fill="none" stroke="#ede8e2" strokeWidth="6" />
                    <motion.circle
                      cx="31"
                      cy="31"
                      r="26"
                      fill="none"
                      stroke="#9a5b43"
                      strokeWidth="6"
                      strokeLinecap="round"
                      initial={false}
                      animate={{ pathLength: step >= STEP.stats ? 0.7 : 0 }}
                      transition={{ duration: step >= STEP.stats ? 1.2 : 0.2, ease: EASE_OUT_EXPO }}
                    />
                  </svg>
                  <span className="absolute inset-0 flex flex-col items-center justify-center leading-none">
                    <span className="text-[16px] font-bold text-[#202020]">
                      <AnimatedNumber value={step >= STEP.stats ? 7 : 0} />
                    </span>
                    <span className="mt-0.5 text-[8.5px] text-[#8a857c]">{copy.of10}</span>
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-[11.5px] font-semibold leading-snug text-[#202020]">{copy.packName}</p>
                  <p className="mt-1 text-[10px] leading-snug text-[#8a857c]">{copy.sessionsLeft}</p>
                </div>
              </div>
            </div>

            <div className="flex-1 rounded-[12px] border border-[#ebe7e1] px-3 py-2.5">
              <p className="mb-2 text-[10.5px] text-[#8a857c]">{copy.activity}</p>
              <div className="space-y-[7px]">
                {activity.map(({ Icon, text, time, tint }, index) => (
                  <Reveal
                    key={text}
                    show={step >= STEP.activity}
                    delay={index * 0.35}
                    x={rtl ? 10 : -10}
                    y={0}
                    className="flex items-center gap-2.5"
                  >
                    <span className={cn('flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full', tint)}>
                      <Icon className="h-3 w-3" />
                    </span>
                    <span className="flex-1 truncate text-[11px] text-[#202020]">{text}</span>
                    <span className="shrink-0 text-[10px] text-[#a8a298]">{time}</span>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>

          <AssistantPanel copy={copy} step={step} rtl={rtl} cycle={timeline.cycle} locale={locale} />
        </div>
      </AdminShell>
    </DemoStage>
  );
}

function AssistantPanel({
  copy,
  step,
  rtl,
  cycle,
  locale,
}: {
  copy: { [K in keyof typeof COPY]: string };
  step: number;
  rtl: boolean;
  cycle: number;
  locale: DemoProps['locale'];
}) {
  const typing = step === STEP.typing;
  const sent = step >= STEP.sent;
  const answered = step >= STEP.answer;
  const campaignSent = step >= STEP.campaign;

  return (
    <div className="flex w-[236px] shrink-0 flex-col rounded-[14px] border border-[#ebe7e1] bg-[#f7f6f4] p-3">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-[#202020] text-white">
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <div className="leading-tight">
          <p className="text-[12px] font-semibold text-[#202020]">{copy.ai}</p>
          <p className="text-[9.5px] text-[#8a857c]">{copy.aiSub}</p>
        </div>
      </div>

      <div className="flex flex-1 flex-col justify-end gap-2 pb-2.5 pt-3">
        {sent && (
          <motion.div
            className="flex justify-end"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE_OUT_EXPO }}
          >
            <span className="max-w-[88%] rounded-[12px] rounded-ee-[4px] bg-[#202020] px-2.5 py-1.5 text-[11px] text-white">
              {copy.question}
            </span>
          </motion.div>
        )}

        {step === STEP.sent && (
          <motion.span
            className="flex w-fit items-center rounded-[12px] bg-white px-3 py-2 ring-1 ring-[#ebe7e1]"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <TypingDots />
          </motion.span>
        )}

        {answered && (
          <motion.div
            className="rounded-[12px] bg-white p-2.5 ring-1 ring-[#ebe7e1]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
          >
            <p className="text-[11px] font-medium leading-snug text-[#202020]">{copy.answer}</p>
            <div className="mt-2 space-y-1.5">
              {QUIET_MEMBERS.map((member, index) => (
                <motion.div
                  key={member.initials}
                  className="flex items-center gap-2"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.15 }}
                >
                  <Avatar initials={member.initials} tone={member.tone} size={20} className="ring-0" />
                  <span className="flex-1 text-[10.5px] text-[#202020]">{member.name[locale]}</span>
                  <span className="text-[9.5px] text-[#a8a298]">{member.days[locale]}</span>
                </motion.div>
              ))}
              <p className="text-[9.5px] text-[#8a857c]">{copy.more}</p>
            </div>

            <motion.div
              className="relative mt-2.5 flex h-8 items-center justify-center gap-1.5 overflow-hidden rounded-[9px] px-2 text-[10px] font-medium"
              initial={false}
              animate={{
                backgroundColor: campaignSent ? DEMO_COLORS.mint : DEMO_COLORS.whatsapp,
                color: campaignSent ? DEMO_COLORS.success : '#ffffff',
                scale: step === STEP.campaign ? [1, 0.95, 1] : 1,
              }}
              transition={{ duration: 0.35, delay: campaignSent ? 0.35 : 0 }}
            >
              <TapRipple
                key={`crm-ripple-${cycle}`}
                show={step === STEP.campaign}
                className="left-1/2 top-1/2"
                color="rgba(255,255,255,0.45)"
              />
              {campaignSent ? (
                <>
                  <CheckBadge show size={15} delay={0.45} />
                  <span className="whitespace-nowrap">{copy.sentLabel}</span>
                </>
              ) : (
                <>
                  <MessageCircle className="h-3 w-3 shrink-0" />
                  <span className="whitespace-nowrap">{copy.send}</span>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>

      <div className="flex h-9 items-center gap-2 rounded-[10px] border border-[#ebe7e1] bg-white ps-2.5 pe-1">
        <span className="relative min-w-0 flex-1 overflow-hidden whitespace-nowrap text-[11px]">
          {typing ? (
            <motion.span
              key={`typing-${cycle}`}
              className="block text-[#202020]"
              initial={{ clipPath: rtl ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)' }}
              animate={{ clipPath: 'inset(0 0 0 0)' }}
              transition={{ duration: 1.1, ease: 'linear' }}
            >
              {copy.question}
            </motion.span>
          ) : (
            <span className="text-[#a8a298]">{copy.placeholder}</span>
          )}
        </span>
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] transition-colors duration-300',
            typing ? 'bg-[#202020] text-white' : 'bg-[#ede8e2] text-[#a8a298]',
          )}
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </span>
      </div>
    </div>
  );
}

function Tag({ className, children }: React.PropsWithChildren<{ className: string }>) {
  return (
    <span className={cn('rounded-full px-2 py-[2px] text-[9.5px] font-medium', className)}>
      {children}
    </span>
  );
}

function StatTile({ label, children }: React.PropsWithChildren<{ label: string }>) {
  return (
    <div className="flex flex-col justify-center rounded-[12px] border border-[#ebe7e1] px-3">
      <span className="text-[10px] text-[#8a857c]">{label}</span>
      <span className="mt-0.5 text-[17px] font-semibold leading-tight text-[#202020]">{children}</span>
    </div>
  );
}
